import React, { useMemo, useRef } from 'react';
import { Line } from 'react-konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useDragGesture } from 'src/hooks/useDragGesture';

interface Point {
    x: number;
    y: number;
}

interface Props {
    points: Point[];
    rectanglePoints: Point[];
    onDragStart: () => void;
    onDragMove: (points: Point[]) => void;
    onDragEnd: (points: Point[]) => void;
    offset: Point;
    width?: number;
    height?: number;
    color?: string;
}

const Border: React.FC<Props> = ({
    points,
    rectanglePoints,
    onDragStart: onDragStartProp,
    onDragMove: onDragMoveProp,
    onDragEnd: onDragEndProp,
    offset,
    width,
    height,
    color,
}) => {
    const reducedPoints = useMemo(
        () => rectanglePoints.reduce<number[]>((array, point) => array.concat([point.x, point.y]), []),
        [rectanglePoints],
    );

    // Snapshot-at-drag-start bookkeeping (the initial mouse position the gesture started from) --
    // can't be derived from the current render, so it stays in a ref. `localPointsRef` holds the
    // most recently computed drag position so `onDragEnd` can commit it -- this replaces a
    // module-level `let localPoints` variable the original had, which was shared across *every*
    // instance of this component rather than scoped to one. That was harmless only because
    // there's realistically one Cropping rectangle on screen at a time; it wasn't a safe pattern.
    const initialPositionRef = useRef<Point>({ x: 0, y: 0 });
    const localPointsRef = useRef<Point[]>(points);
    // `points` as they were when the drag began: deltas are measured from the initial mouse position,
    // so they must be applied to the start points, not the live prop that onDragMove keeps updating.
    const startPointsRef = useRef<Point[]>(points);

    const handleDragMove = (e: Event) => {
        const evt = e as MouseEvent;
        const { x: initialX, y: initialY } = initialPositionRef.current;
        const deltaX = initialX - evt.clientX;
        const deltaY = initialY - evt.clientY;

        const newPoints = startPointsRef.current.map((point) => ({ x: point.x - deltaX, y: point.y - deltaY }));

        if (width) {
            // ensure point never goes outside of width bounds
            const boxWidth = newPoints[1].x - newPoints[0].x;
            const rightBound = width + offset.x;

            if (newPoints[1].x > rightBound) {
                newPoints[0].x = rightBound - boxWidth;
                newPoints[1].x = rightBound;
            } else if (newPoints[0].x < offset.x) {
                newPoints[0].x = offset.x;
                newPoints[1].x = offset.x + boxWidth;
            }
        }

        if (height) {
            // ensure point never goes outside of height bounds
            const boxHeight = newPoints[1].y - newPoints[0].y;
            const bottomBound = height + offset.y;
            if (newPoints[1].y > bottomBound) {
                newPoints[0].y = bottomBound - boxHeight;
                newPoints[1].y = bottomBound;
            } else if (newPoints[0].y < offset.y) {
                newPoints[0].y = offset.y;
                newPoints[1].y = offset.y + boxHeight;
            }
        }

        localPointsRef.current = newPoints;
        onDragMoveProp(newPoints);
    };

    const { start } = useDragGesture({
        onDragMove: handleDragMove,
        onDragEnd: () => onDragEndProp(localPointsRef.current),
    });

    const onDragStart = (e: KonvaEventObject<MouseEvent>) => {
        initialPositionRef.current = { x: e.evt.clientX, y: e.evt.clientY };
        startPointsRef.current = points;
        // a click without a move commits the unchanged crop, not the points from mount
        localPointsRef.current = points;
        start();
        e.cancelBubble = true;
        onDragStartProp();
    };

    return (
        <Line
            points={reducedPoints}
            stroke={color || 'black'}
            strokeWidth={1}
            closed={true}
            onMouseDown={onDragStart}
        />
    );
};

export default Border;
